import { Badge } from '@/components/ui/badge'
import { Server, Cpu, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface InfrastructureNodeData {
  readonly id: string
  readonly type: string
  readonly name?: string
  readonly host: string
  readonly port: number
  readonly status: string
  readonly version?: string
  readonly lastHeartbeat?: string
}

export interface InfrastructureStatusCardData {
  readonly message: string
  readonly nodes: readonly InfrastructureNodeData[]
}

const STATUS_STYLES: Record<string, { badge: string; dot: string }> = {
  HEALTHY: {
    badge: 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50',
    dot: 'bg-emerald-500',
  },
  ACTIVE: {
    badge: 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50',
    dot: 'bg-emerald-500',
  },
  DEGRADED: {
    badge: 'border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50',
    dot: 'bg-amber-500',
  },
  STALE: {
    badge: 'border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/50',
    dot: 'bg-amber-500',
  },
  DOWN: {
    badge: 'border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50',
    dot: 'bg-red-500',
  },
  UNREACHABLE: {
    badge: 'border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50',
    dot: 'bg-red-500',
  },
}

const DEFAULT_STATUS = STATUS_STYLES.DEGRADED

const TYPE_STYLES: Record<string, string> = {
  PDP: 'text-blue-600 dark:text-blue-400',
  PEP: 'text-purple-600 dark:text-purple-400',
}

export function InfrastructureStatusCard({ data }: { data: InfrastructureStatusCardData }) {
  if (data.nodes.length === 0) {
    return (
      <div className="my-2 rounded-lg border border-border dark:border-border/60 bg-card shadow-sm px-3.5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Server className="size-4" />
          <span>{data.message}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="my-2 rounded-lg border border-border dark:border-border/60 bg-card shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <Server className="size-3.5 text-indigo-600 dark:text-indigo-400" />
        <span className="text-xs font-medium text-foreground">{data.message}</span>
      </div>
      <div className="divide-y divide-border/50">
        {data.nodes.map((node) => (
          <InfrastructureNodeRow key={node.id} node={node} />
        ))}
      </div>
    </div>
  )
}

function InfrastructureNodeRow({ node }: { node: InfrastructureNodeData }) {
  const statusStyle = STATUS_STYLES[node.status] ?? DEFAULT_STATUS
  const typeStyle = TYPE_STYLES[node.type] ?? 'text-muted-foreground'

  return (
    <div className="flex items-center gap-3 px-3.5 py-2.5">
      <div className="flex items-center justify-center size-6 rounded-md shrink-0 bg-muted">
        <Cpu className="size-3 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={cn('size-2 rounded-full shrink-0', statusStyle.dot)} />
          <span className="text-xs font-medium text-foreground">
            {node.name ?? `${node.host}:${node.port}`}
          </span>
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0 font-semibold', statusStyle.badge)}>
            {node.status}
          </Badge>
          <span className={cn('text-[10px] font-mono font-medium', typeStyle)}>{node.type}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
          <span className="font-mono">{node.host}:{node.port}</span>
          {node.version && <span>v{node.version}</span>}
          {node.lastHeartbeat && (
            <span className="flex items-center gap-1">
              <Clock className="size-2.5" />
              {new Date(node.lastHeartbeat).toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
